import { useState, useEffect, useRef, useLayoutEffect, MutableRefObject } from "react"

const useIsoLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const getWindowDimensions = () => {
  if(typeof window !== "undefined") {
    const { innerWidth: width, innerHeight: height } = window
    return {
      width,
      height,
    }
  }

  return {
    width: 0,
    height: 0,
  }
}

const useWindowDimensions = () => {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  )

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  return windowDimensions
}

const useDebounce = <T>(value: T, delay?: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay ?? 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const usePrevious = <T>(value: T): T => {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref: any = useRef<T>(undefined);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) {
      setMatches(media.matches);
    }
    const listener = () => setMatches(media.matches);
    window.addEventListener("resize", listener);
    return () => window.removeEventListener("resize", listener);
  }, [matches, query]);

  return matches;
}

const useRefScrollProgress = (watch?: unknown): [MutableRefObject<HTMLDivElement>, number, number, number, number] => {
  const ref = useRef<HTMLDivElement>(null!)
  const [startProgress, setStartProgress] = useState(0)
  const [endProgress, setEndProgress] = useState(0)
  const [start, setStart] = useState(0)
  const [end, setEnd] = useState(0)

  const { width, height } = useWindowDimensions();
  
  useEffect(() => {
    const rect = ref.current.getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop
    const offsetTop = rect.top + scrollTop

    setStart(offsetTop);
    setStartProgress(offsetTop / document.body.clientHeight);
    
    setEnd(offsetTop + rect.height);
    setEndProgress((offsetTop + rect.height) / document.body.clientHeight);
  }, [watch, width, height])

  return [ref, startProgress, endProgress, start, end]
}

export { useDebounce, useWindowDimensions, usePrevious, useMediaQuery, useRefScrollProgress, useIsoLayoutEffect }